import { isRecord } from './http.js';
import { isOnlineGame } from './types.js';
import type {
  AcceptedInvite,
  Friend,
  FriendSearchResult,
  Invite,
  InviteStatus,
  SentInvite,
} from './types.js';

/**
 * Narrowing for every response body. The Worker and this package are versioned
 * together, but a stale cached bundle or an intermediary error page can still
 * deliver something else — so every field is checked and a mismatch becomes an
 * `unknown` SocialError rather than a runtime crash deep in a UI render.
 */

const INVITE_STATUSES: readonly string[] = ['pending', 'accepted', 'declined'];

function isInviteStatus(value: unknown): value is InviteStatus {
  return typeof value === 'string' && INVITE_STATUSES.includes(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function nullableStr(value: unknown): string | null | undefined {
  if (value === null || typeof value === 'string') return value;
  return undefined;
}

function num(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

export function toFriend(raw: unknown): Friend | null {
  if (!isRecord(raw)) return null;
  const userId = str(raw['userId']);
  const displayName = str(raw['displayName']);
  const avatarUrl = str(raw['avatarUrl']);
  const handle = nullableStr(raw['handle']);
  if (userId === null || displayName === null || avatarUrl === null) return null;
  if (handle === undefined) return null;
  return { userId, handle, displayName, avatarUrl };
}

export function toSearchResult(raw: unknown): FriendSearchResult | null {
  const friend = toFriend(raw);
  if (friend === null || !isRecord(raw)) return null;
  const following = raw['following'];
  if (typeof following !== 'boolean') return null;
  return { ...friend, following };
}

export function toSentInvite(raw: unknown): SentInvite | null {
  if (!isRecord(raw)) return null;
  const id = str(raw['id']);
  const roomCode = str(raw['roomCode']);
  const createdAt = num(raw['createdAt']);
  const expiresAt = num(raw['expiresAt']);
  const status = raw['status'];
  const game = raw['game'];
  if (id === null || roomCode === null || createdAt === null || expiresAt === null) return null;
  if (!isInviteStatus(status) || !isOnlineGame(game)) return null;
  return { id, game, roomCode, status, createdAt, expiresAt };
}

export function toInvite(raw: unknown): Invite | null {
  const base = toSentInvite(raw);
  if (base === null || !isRecord(raw)) return null;
  const from = toFriend(raw['from']);
  if (from === null) return null;
  return { ...base, from };
}

export function toAcceptedInvite(raw: unknown): AcceptedInvite | null {
  if (!isRecord(raw)) return null;
  const roomCode = str(raw['roomCode']);
  const game = raw['game'];
  if (roomCode === null || !isOnlineGame(game)) return null;
  return { game, roomCode };
}

/** Unwraps `{ <key>: [...] }` envelopes, rejecting the whole list on one bad item. */
export function toList<T>(
  raw: unknown,
  key: string,
  item: (entry: unknown) => T | null
): T[] | null {
  if (!isRecord(raw)) return null;
  const entries = raw[key];
  if (!Array.isArray(entries)) return null;
  const decoded: T[] = [];
  for (const entry of entries) {
    const value = item(entry);
    if (value === null) return null;
    decoded.push(value);
  }
  return decoded;
}

/** For endpoints whose success body carries nothing the caller needs. */
export function toVoid(): void {
  return undefined;
}

/** Unwraps a `{ <key>: {...} }` envelope. */
export function toWrapped<T>(
  raw: unknown,
  key: string,
  item: (entry: unknown) => T | null
): T | null {
  if (!isRecord(raw)) return null;
  return item(raw[key]);
}
